import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Box, { BoxProps } from '@mui/material/Box'
import { Control, Controller, FieldErrors, UseFormGetValues, UseFormHandleSubmit, UseFormSetValue } from 'react-hook-form'
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { UpdateRequest } from './GlobalUpdateTypes';
import useOptions, { StageOption } from '../../hooks/useOptions';
import { isEmpty } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { client } from '../../utils/axios-client'
import CustomDatePicker from '../common/CustomDatePicker'
import NullableDatePicker from '../common/NullableDataPicker'


type LeadFormProps = {
    onSubmit: any,
    getValues: UseFormGetValues<UpdateRequest>,
    setValue: UseFormSetValue<UpdateRequest>,
    control: Control<UpdateRequest, any>,
    errors: FieldErrors<UpdateRequest>
    handleClose: () => void,
    updateRequest: UpdateRequest
}

const UpdateFieldsForm = ({ onSubmit, control, errors, handleClose, updateRequest, setValue, getValues }: LeadFormProps) => {
    const [playbookCadenceId, setPlaybookCadenceId] = useState('');
    const { options: industries } = useOptions('industry');
    const { options: countries } = useOptions('country');
    const { options: levels } = useOptions('level');
    const { options: functions } = useOptions('function');
    const { options: sources } = useOptions('source');
    const { options: accounts } = useOptions('account');
    const { options: stages } = useOptions('stage');
    const { options: regions } = useOptions('region');
    const { options: owners } = useOptions('owner');
    const { options: activities } = useOptions('activity')    
    const [playbookCadenceSteps, setPlaybookCadenceSteps] = useState<StageOption[]>([]);
    const { options: customers } = useOptions('customers')
    const [customerId, setCustomerId] = useState<string | null>(null);
    const customerParams = useMemo(() => { return { params: { customerId: customerId } }; }, [customerId]);
    const { options: playbookCadences } = useOptions('playbookcadenceswithplaybook', customerParams);        
    const { options: sprints } = useOptions('customersprint', customerParams);

    useEffect(() => {
        let subscribed = true;

        const loadCadenceSteps = async () => {
            if (playbookCadenceId === '') return;

            const response = await client.get(`/api/options/playbooks/cadences/${playbookCadenceId}/steps`);

            if (subscribed) {
                setPlaybookCadenceSteps(response.data);
            }
        }

        loadCadenceSteps();

        return () => { subscribed = false; }
    }, [playbookCadenceId]);



    return <>
        <PerfectScrollbar options={{ wheelPropagation: false }} >
            <Box sx={{ p: 5 }}>
                <form noValidate autoComplete='off' onSubmit={onSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='accountId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Account"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {accounts.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Account*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='customerId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Customer"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => { onChange(e.target.value === '' ? null : e.target.value); setCustomerId(e.target.value === '' ? null : e.target.value) }}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {customers.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Customer*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='leadStageId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Stage"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {stages.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />

                            {/*Stage*/}
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='sprintId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Sprint"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {sprints.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <TextField
                                select
                                label="Playbook Cadence"
                                InputProps={{ style: { border: !isEmpty(playbookCadenceId) ? '1px solid green' : undefined } }}
                                value={playbookCadenceId}
                                onChange={(e) => { setPlaybookCadenceId(e.target.value); setValue('nextActivityCadenceStepId', null) }}
                                size="small"
                                SelectProps={{
                                    displayEmpty: true
                                }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value=''>No Change</MenuItem>
                                {playbookCadences.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='nextActivityCadenceStepId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        disabled={playbookCadenceId === ''}
                                        select
                                        label="Next Cadence Step"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {playbookCadenceSteps.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='nextActivityId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Next Activity"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {activities.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='nextActivityDateTime'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <NullableDatePicker sx={{ border: !isEmpty(value) ? '1px solid green' : undefined }} error={false} value={value} onChange={(newValue) => onChange(newValue)} label="Next Activity Date" />
                                    //<TextField
                                    //    select
                                    //    label="Next Activity"
                                    //    InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                    //    value={value}
                                    //    onBlur={onBlur}
                                    //    onChange={onChange}
                                    //    size="small"
                                    //    SelectProps={{
                                    //        displayEmpty: true
                                    //    }}
                                    //    InputLabelProps={{ shrink: true }}
                                    //>
                                    //    <MenuItem value=''>No Change</MenuItem>
                                    //    {activities.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    //</TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='nextActivityOwnerId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Next Activity Owner"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {owners.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Source*/}
                        </Grid>

                        <Grid item xs={12} sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Divider />
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='salutation'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Salutation'
                                            placeholder='Salutation'
                                            inputProps={{ 'data-testid': 'salutation' }}
                                            error={Boolean(errors.salutation)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.salutation && <FormHelperText sx={{ color: 'error.main' }}>{errors.salutation.message}</FormHelperText>}
                            </FormControl> {/*Salutation*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='jobTitle'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Job Title'
                                            placeholder='Job Title'
                                            inputProps={{ 'data-testid': 'job-title' }}
                                            error={Boolean(errors.jobTitle)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.jobTitle && <FormHelperText sx={{ color: 'error.main' }}>{errors.jobTitle.message}</FormHelperText>}
                            </FormControl> {/*Job Title*/}
                        </Grid>

                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='company'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Company'
                                            placeholder='Company'
                                            inputProps={{ 'data-testid': 'company' }}
                                            error={Boolean(errors.company)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.company && <FormHelperText sx={{ color: 'error.main' }}>{errors.company.message}</FormHelperText>}
                            </FormControl> {/*Company*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='addressStreet'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Street Address'
                                            placeholder='Street Address'
                                            inputProps={{ 'data-testid': 'addressStreet' }}
                                            error={Boolean(errors.addressStreet)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.addressStreet && <FormHelperText sx={{ color: 'error.main' }}>{errors.addressStreet.message}</FormHelperText>}
                            </FormControl> {/*Address Street*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='addressCity'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='City'
                                            placeholder='City'
                                            inputProps={{ 'data-testid': 'addressCity' }}
                                            error={Boolean(errors.addressCity)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.addressCity && <FormHelperText sx={{ color: 'error.main' }}>{errors.addressCity.message}</FormHelperText>}
                            </FormControl> {/*Address City*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='addressProvince'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Province'
                                            placeholder='Province'
                                            inputProps={{ 'data-testid': 'addressProvince' }}
                                            error={Boolean(errors.addressProvince)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.addressProvince && <FormHelperText sx={{ color: 'error.main' }}>{errors.addressProvince.message}</FormHelperText>}
                            </FormControl> {/*Address Province*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >

                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='addressPostalCode'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Postal Code'
                                            placeholder='Postal Code'
                                            inputProps={{ 'data-testid': 'addressPostalCode' }}
                                            error={Boolean(errors.addressPostalCode)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.addressPostalCode && <FormHelperText sx={{ color: 'error.main' }}>{errors.addressPostalCode.message}</FormHelperText>}
                            </FormControl> {/*Address Postal Code*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='countryId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Country"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {countries.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Address Country*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='addressRegionId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Region"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {regions.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Address Region*/}
                        </Grid>
                        <Grid item xs={12} sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Divider />
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='directPhone'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Direct Phone'
                                            placeholder='Direct Phone'
                                            inputProps={{ 'data-testid': 'directPhone' }}
                                            error={Boolean(errors.directPhone)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.directPhone && <FormHelperText sx={{ color: 'error.main' }}>{errors.directPhone.message}</FormHelperText>}
                            </FormControl> {/*Direct*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='dncPhone'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="DNC Phone"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value === null ? '' : value ? "yes" : "no"}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === "yes" ? true : false)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        <MenuItem value='no'>No</MenuItem>
                                        <MenuItem value='yes'>Yes</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='mobilePhone'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Mobile Phone'
                                            placeholder='Mobile Phone'
                                            inputProps={{ 'data-testid': 'mobilePhone' }}
                                            error={Boolean(errors.mobilePhone)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.mobilePhone && <FormHelperText sx={{ color: 'error.main' }}>{errors.mobilePhone.message}</FormHelperText>}
                            </FormControl> {/*Mobile*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='dncMobile'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="DNC Mobile"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value === null ? '' : value ? "yes" : "no"}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === "yes" ? true : false)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        <MenuItem value='no'>No</MenuItem>
                                        <MenuItem value='yes'>Yes</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='switchboardPhone'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Switchboard'
                                            placeholder='Switchboard'
                                            inputProps={{ 'data-testid': 'switchboardPhone' }}
                                            error={Boolean(errors.switchboardPhone)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.switchboardPhone && <FormHelperText sx={{ color: 'error.main' }}>{errors.switchboardPhone.message}</FormHelperText>}
                            </FormControl> {/*Switchboard*/}

                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='dncSwitchboard'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="DNC Switchboard"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value === null ? '' : value ? "yes" : "no"}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === "yes" ? true : false)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        <MenuItem value='no'>No</MenuItem>
                                        <MenuItem value='yes'>Yes</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >

                        </Grid>
                        <Grid item xs={7} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='website'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Website'
                                            placeholder='Website'
                                            inputProps={{ 'data-testid': 'website' }}
                                            error={Boolean(errors.website)}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />
                                {errors.website && <FormHelperText sx={{ color: 'error.main' }}>{errors.website.message}</FormHelperText>}
                            </FormControl>{/*Website*/}
                        </Grid>

                        <Grid item xs={5} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <Controller
                                    name='switchboardEmail'
                                    control={control}

                                    render={({ field: { value, onChange } }) => (
                                        <TextField
                                            value={value ?? ''}
                                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                            size='small'
                                            label='Switchboard Email'
                                            placeholder='Switchboard Email'
                                            inputProps={{ 'data-testid': 'switchboardEmail' }}
                                            style={{ border: !isEmpty(value) ? '1px solid green' : undefined }}
                                        />
                                    )}
                                />

                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Divider />
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='industryId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Industry"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {industries.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Industry*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='leadLevelId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Level"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {levels.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Level*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='leadFunctionId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Function"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {functions.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Function*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }} >
                            <Controller
                                name='ownerId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Owner"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {owners.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Owner*/}
                        </Grid>
                        <Grid item xs={4} sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }} >
                            <Controller
                                name='leadSourceId'
                                control={control}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        select
                                        label="Source"
                                        InputProps={{ style: { border: !isEmpty(value) ? '1px solid green' : undefined } }}
                                        value={value ?? ''}
                                        onBlur={onBlur}
                                        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                        size="small"
                                        SelectProps={{
                                            displayEmpty: true
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value=''>No Change</MenuItem>
                                        {sources.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                    </TextField>
                                )}
                            />
                            {/*Source*/}
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </PerfectScrollbar>

    </>
}

export default UpdateFieldsForm